import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Transition as _Transition, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "q-mb-lg q-pa-md bg-grey-2 rounded-borders"
}
const _hoisted_2 = { class: "row q-gutter-sm justify-center" }
const _hoisted_3 = { class: "full-width" }
const _hoisted_4 = {
  key: 0,
  class: "q-mb-md"
}
const _hoisted_5 = { class: "text-small" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "text-body2" }
const _hoisted_8 = {
  key: 0,
  class: "q-mb-xs"
}
const _hoisted_9 = {
  key: 0,
  class: "q-mb-xs"
}
const _hoisted_10 = {
  key: 0,
  class: "q-mb-xs"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 2,
  class: "column items-center q-py-xl"
}
const _hoisted_13 = {
  key: 3,
  class: "column items-center q-py-xl"
}
const _hoisted_14 = {
  key: 0,
  class: "row justify-end q-gutter-sm"
}
const _hoisted_15 = {
  key: 1,
  class: "row justify-center"
}

import ApiForm from 'components/qutils/form/ApiForm.vue'
import { useDialogPluginComponent, useQuasar } from 'quasar'
import BaseDialogContent from 'src/apps/core/components/BaseDialogContent.vue'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import AnonymousLeads from '../../api/anonymous-leads'

// Dialog composable
const CACHE_KEY = 'nonMemberLeadFormOptions'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes


export default /*@__PURE__*/_defineComponent({
  __name: 'AnonymousLeadModal',
  props: {
  advertUuid: {
    type: String,
    required: true,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent()

// Props
const props = __props

// Emits


// State
const $q = useQuasar()
const $route = useRoute()
const loading = ref(true)
const submitting = ref(false)
const submitted = ref(false)
const errors = ref<any>()
const formRef = ref<any>()

// Form data - ApiForm compatible structure
const formData = reactive({
  advert: props.advertUuid,
  email: '',
  name: '',
  phone: '',
  message: '',
  consent_data_processing: false,
  consent_marketing: false,
})

// Form configuration from backend (ApiForm expects OPTIONS response structure)
const formFields = ref<any>(null)
const throttleInfo = ref<any>(null)

// Fields configuration to customize ApiForm behavior
const fieldsConfig = computed(() => ({
  advert: {
    attrs: {
      type: 'hidden',
    },
  },
  message: {
    attrs: {
      type: 'textarea',
      rows: 4,
    },
  },
  email: {
    attrs: {
      type: 'email',
    },
  },
  phone: {
    attrs: {
      type: 'tel',
    },
  },
}))

// Enhanced form validation with all requirements
const isFormValid = computed(() => {
  return !!(
    formData.email &&
    formData.message &&
    formData.message.length >= 10 &&
    formData.consent_data_processing
  )
})

// Form submission method for footer button
const submitForm = () => {
  // Trigger the form's native submit event
  if (formRef.value?.$el) {
    const form = formRef.value.$el
    form.requestSubmit()
  }
}

// Form options caching using Quasar LocalStorage (5 minutes)
const getCachedFormOptions = () => {
  try {
    const cached = $q.localStorage.getItem(CACHE_KEY) as any
    if (
      cached &&
      typeof cached === 'object' &&
      cached.timestamp &&
      cached.data
    ) {
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data
      }
      // Clear expired cache
      $q.localStorage.remove(CACHE_KEY)
    }
  } catch (err) {
    console.warn('Cache retrieval failed:', err)
    $q.localStorage.remove(CACHE_KEY)
  }
  return null
}

const setCachedFormOptions = (data: any) => {
  try {
    $q.localStorage.set(CACHE_KEY, {
      data,
      timestamp: Date.now(),
    })
  } catch (err) {
    console.warn('Cache storage failed, continuing without cache:', err)
  }
}

// Load form options with caching
onMounted(async () => {
  // Try cache first
  const cachedOptions = getCachedFormOptions()
  if (cachedOptions) {
    formFields.value = cachedOptions.actions?.POST
    throttleInfo.value = cachedOptions.throttle_info
    loading.value = false
    return
  }

  // Fetch from API if not cached
  try {
    const response = await AnonymousLeads.getFormOptions()
    formFields.value = response.data.actions?.POST
    throttleInfo.value = response.data.throttle_info

    // Cache the response
    setCachedFormOptions(response.data)
  } catch (err) {
    console.error('Failed to load form options:', err)
    $q.notify({
      message:
        'Formular konnte nicht geladen werden. Bitte versuche es später erneut.',
      type: 'negative',
    })
    onDialogCancel()
  } finally {
    loading.value = false
  }
})

// ApiForm event handlers
const onFormSubmit = async (data: any) => {
  submitting.value = true
  errors.value = undefined // Clear previous errors

  try {
    await AnonymousLeads.createAnonymousLead(data)

    submitted.value = true
  } catch (err: any) {
    console.error('Failed to submit anonymous lead:', err)

    // Handle rate limiting (429) specifically
    if (err.response?.status === 429) {
      // Calculate retry datetime from retry-after header
      const retryAfter = err.response.headers?.['retry-after']
      let retryDatetime = ''

      if (retryAfter) {
        const retryAfterSeconds = parseInt(retryAfter)
        const retryDate = new Date(Date.now() + retryAfterSeconds * 1000)
        retryDatetime = retryDate.toLocaleString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } // Update throttle info to reflect current state first
      if (throttleInfo.value) {
        throttleInfo.value.is_throttled = true
        // Try to extract retry time from response if available
        if (retryAfter) {
          throttleInfo.value.retry_after_seconds = parseInt(retryAfter)
        } else {
          // Fallback: estimate 1 hour based on the 3/h rate limit
          throttleInfo.value.retry_after_seconds = 3600 // 1 hour
        }
      }

      // If we still don't have a datetime, try using the updated throttleInfo
      if (!retryDatetime && throttleInfo.value?.retry_after_seconds) {
        const retryDate = new Date(
          Date.now() + throttleInfo.value.retry_after_seconds * 1000
        )
        retryDatetime = retryDate.toLocaleString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      }

      $q.notify({
        message: 'Du hast das Nachrichtenlimit erreicht',
        caption: retryDatetime
          ? `Du kannst wieder ab ${retryDatetime} eine Nachricht senden oder registriere dich für unbegrenzte Nachrichten.`
          : 'Bitte versuche es später erneut.',
        type: 'warning',
        icon: 'schedule',
        timeout: 0,
        actions: [
          {
            label: 'OK',
            color: 'primary',
            handler: () => {
              /* dismiss */
            },
          },
        ],
      })
    }
    // Handle API validation errors
    else if (err.response?.data) {
      errors.value = err.response.data
    }
    // Generic error notification for other errors
    else {
      $q.notify({
        message:
          'Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut.',
        type: 'negative',
        icon: 'error',
      })
    }
  } finally {
    submitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_banner = _resolveComponent("q-banner")!
  const _component_q_slide_transition = _resolveComponent("q-slide-transition")!
  const _component_q_spinner_dots = _resolveComponent("q-spinner-dots")!
  const _component_q_linear_progress = _resolveComponent("q-linear-progress")!
  const _component_q_avatar = _resolveComponent("q-avatar")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_spinner_gears = _resolveComponent("q-spinner-gears")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    maximized: _unref($q).screen.lt.sm,
    "no-backdrop-dismiss": "",
    onHide: _unref(onDialogHide)
  }, {
    default: _withCtx(() => [
      _createVNode(BaseDialogContent, {
        "header-label": "Nachricht senden",
        style: {"min-width":"60vw","max-width":"500px"}
      }, {
        default: _withCtx(() => [
          (!loading.value && !submitted.value && formFields.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-center q-mb-md" }, [
                  _createElementVNode("div", { class: "text-h4 q-my-sm" }, "Noch nicht registriert?"),
                  _createElementVNode("div", { class: "text-body2 text-grey-7" }, " Erhöhe deine Chancen auf eine Antwort und entdecke die Vorteile einer Mitgliedschaft. ")
                ], -1)),
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_q_btn, {
                    rounded: "",
                    "no-caps": "",
                    unelevated: "",
                    color: "primary",
                    label: "Anmelden",
                    to: { name: 'login', query: { n: _unref($route).fullPath } },
                    onClick: _unref(onDialogCancel)
                  }, null, 8, ["to", "onClick"]),
                  _createVNode(_component_q_btn, {
                    rounded: "",
                    "no-caps": "",
                    unelevated: "",
                    color: "secondary",
                    label: "Registrieren",
                    to: { name: 'signup-private', query: { n: _unref($route).fullPath } },
                    onClick: _unref(onDialogCancel)
                  }, null, 8, ["to", "onClick"])
                ])
              ]))
            : _createCommentVNode("", true),
          (!loading.value && !submitted.value && formFields.value)
            ? (_openBlock(), _createBlock(ApiForm, {
                key: 1,
                ref_key: "formRef",
                ref: formRef,
                modelValue: formData,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData) = $event)),
                "hide-submit": "",
                outlined: "",
                "option-fields": formFields.value,
                "exclude-fields": ['advert'],
                "fields-config": fieldsConfig.value,
                errors: errors.value,
                loading: submitting.value,
                disabled: submitted.value,
                onSubmit: onFormSubmit
              }, {
                extras: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    (throttleInfo.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _createVNode(_component_q_banner, {
                            dense: "",
                            rounded: "",
                            class: "bg-blue-1 text-primary"
                          }, {
                            avatar: _withCtx(() => [
                              _createVNode(_component_q_icon, { name: "info" })
                            ]),
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_5, [
                                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "q-mb-xs" }, [
                                  _createElementVNode("strong", null, "Nachrichtenlimit ohne Mitgliedschaft")
                                ], -1)),
                                _createElementVNode("div", null, _toDisplayString(throttleInfo.value.description ||
                          `Maximal ${throttleInfo.value.rate} Nachrichten erlaubt`), 1),
                                _cache[3] || (_cache[3] = _createElementVNode("div", null, [
                                  _createTextVNode(" Für "),
                                  _createElementVNode("b", null, "unbegrenzte Nachrichten, Favoriten, Suchaufträge"),
                                  _createTextVNode(" registriere dich jetzt! ")
                                ], -1))
                              ])
                            ]),
                            _: 1
                          })
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_component_q_slide_transition, null, {
                      default: _withCtx(() => [
                        (!isFormValid.value && !submitting.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _createVNode(_Transition, {
                                appear: "",
                                "enter-active-class": "animated fadeIn",
                                "leave-active-class": "animated fadeOut"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_q_banner, {
                                    class: "bg-grey-3 text-dark q-mb-md",
                                    rounded: ""
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_7, [
                                        _createVNode(_component_q_slide_transition, null, {
                                          default: _withCtx(() => [
                                            (!formData.email)
                                              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                                  _createVNode(_component_q_icon, {
                                                    name: "email",
                                                    class: "q-mr-xs"
                                                  }),
                                                  _cache[4] || (_cache[4] = _createTextVNode(" E-Mail-Adresse ist erforderlich ", -1))
                                                ]))
                                              : _createCommentVNode("", true)
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_q_slide_transition, null, {
                                          default: _withCtx(() => [
                                            (!formData.message)
                                              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                                  _createVNode(_component_q_icon, {
                                                    name: "message",
                                                    class: "q-mr-xs"
                                                  }),
                                                  _cache[5] || (_cache[5] = _createTextVNode(" Nachricht ist erforderlich ", -1))
                                                ]))
                                              : _createCommentVNode("", true)
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_q_slide_transition, null, {
                                          default: _withCtx(() => [
                                            (
                                formData.message && formData.message.length < 10
                              )
                                              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                                  _createVNode(_component_q_icon, {
                                                    name: "edit",
                                                    class: "q-mr-xs"
                                                  }),
                                                  _createTextVNode(" Nachricht muss mindestens 10 Zeichen lang sein (" + _toDisplayString(formData.message.length) + "/10) ", 1)
                                                ]))
                                              : _createCommentVNode("", true)
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_q_slide_transition, null, {
                                          default: _withCtx(() => [
                                            (!formData.consent_data_processing)
                                              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                                  _createVNode(_component_q_icon, {
                                                    name: "verified_user",
                                                    class: "q-mr-xs"
                                                  }),
                                                  _cache[6] || (_cache[6] = _createTextVNode(" Datenschutz-Einwilligung ist erforderlich ", -1))
                                                ]))
                                              : _createCommentVNode("", true)
                                          ]),
                                          _: 1
                                        })
                                      ])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                submit: _withCtx(() => [...(_cache[7] || (_cache[7] = []))]),
                _: 1
              }, 8, ["modelValue", "option-fields", "fields-config", "errors", "loading", "disabled"]))
            : (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createVNode(_component_q_spinner_dots, {
                    size: "2rem",
                    color: "primary"
                  }),
                  _cache[8] || (_cache[8] = _createElementVNode("div", { class: "q-mt-md text-subtitle2 text-grey-6" }, " Formular wird geladen... ", -1)),
                  _createVNode(_component_q_linear_progress, {
                    indeterminate: "",
                    color: "primary",
                    class: "q-mt-md",
                    style: {"width":"200px"}
                  })
                ]))
              : (submitted.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createVNode(_component_q_avatar, {
                      size: "4rem",
                      color: "positive",
                      "text-color": "white",
                      class: "q-mb-md"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_icon, {
                          name: "check",
                          size: "2rem"
                        })
                      ]),
                      _: 1
                    }),
                    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-h4 q-mb-sm text-positive" }, "Nachricht gesendet!", -1)),
                    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-body2 text-grey-7 text-center q-px-md q-mt-md" }, " Der Anbieter wird sich bald bei dir melden. Du erhältst eine Benachrichtigung per E-Mail. ", -1)),
                    _createVNode(_component_q_separator, {
                      class: "q-my-md",
                      style: {"width":"60px"}
                    }),
                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "text-caption text-grey-7 text-center" }, " Tipp: Registriere dich für schnellere Kommunikation und weitere Vorteile! ", -1))
                  ]))
                : _createCommentVNode("", true)
        ]),
        footer: _withCtx(() => [
          (!loading.value && !submitted.value && formFields.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createVNode(_component_q_btn, {
                  flat: "",
                  label: "Abbrechen",
                  color: "grey-6",
                  disable: submitting.value,
                  onClick: _unref(onDialogCancel)
                }, null, 8, ["disable", "onClick"]),
                _createVNode(_component_q_btn, {
                  unelevated: "",
                  rounded: "",
                  label: "Nachricht senden",
                  color: "primary",
                  loading: submitting.value,
                  disable: submitting.value || !isFormValid.value,
                  onClick: submitForm
                }, {
                  loading: _withCtx(() => [
                    _createVNode(_component_q_spinner_gears)
                  ]),
                  _: 1
                }, 8, ["loading", "disable"])
              ]))
            : (submitted.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createVNode(_component_q_btn, {
                    unelevated: "",
                    color: "primary",
                    label: "Schließen",
                    onClick: _unref(onDialogOK)
                  }, null, 8, ["onClick"])
                ]))
              : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["maximized", "onHide"]))
}
}

})